import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";
import Pagination from "../../elements/Pagination";

/* ─── CONFIG ─────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  DRAFT:               { label: "New Applications",    short: "New",        cls: "badge-secondary", color: "#8b5cf6", bg: "#f3e8ff", icon: "fa-user-plus"         },
  UNDER_REVIEW:        { label: "Under Review",        short: "Under Review",cls: "badge-warning",   color: "#f97316", bg: "#fff7ed", icon: "fa-clock-o"            },
  CORRECTION_REQUIRED: { label: "Correction Required", short: "Correction",  cls: "badge-danger",    color: "#ef4444", bg: "#fef2f2", icon: "fa-exclamation-circle" },
  APPROVED:            { label: "Approved",            short: "Approved",    cls: "badge-success",   color: "#22c55e", bg: "#f0fdf4", icon: "fa-check-circle"       },
  REJECTED:            { label: "Rejected",            short: "Rejected",    cls: "badge-dark",      color: "#64748b", bg: "#f8fafc", icon: "fa-times-circle"       },
  ACTIVE:              { label: "Active Pandits",      short: "Active",      cls: "badge-primary",   color: "#3b82f6", bg: "#eff6ff", icon: "fa-users"              },
  SUSPENDED:           { label: "Suspended",           short: "Suspended",   cls: "badge-danger",    color: "#ef4444", bg: "#fef2f2", icon: "fa-ban"                },
};

const DOC_STATUS_CONFIG = {
  PENDING:  { label: "Pending",  cls: "badge-warning" },
  APPROVED: { label: "Approved", cls: "badge-success" },
  REJECTED: { label: "Rejected", cls: "badge-danger"  },
};

const DOCUMENTS = [
  { key: "aadhaar",     label: "Aadhaar Card",      urlField: "aadharUrl"         },
  { key: "pan",         label: "PAN Card",           urlField: "panUrl"            },
  { key: "certificate", label: "Priest Certificate", urlField: "certificatePdfUrl" },
];

const STAT_ORDER    = ["DRAFT", "UNDER_REVIEW", "CORRECTION_REQUIRED", "APPROVED", "REJECTED", "ACTIVE"];
const PIPELINE_KEYS = ["DRAFT", "UNDER_REVIEW", "CORRECTION_REQUIRED", "APPROVED", "ACTIVE"];

/* ─── TABLE CELL STYLES ─────────────────────────────────────── */
const TH = {
  padding: "10px 14px", fontSize: "11px", fontWeight: 600, color: "#6b7280",
  textTransform: "uppercase", letterSpacing: "0.4px",
  borderBottom: "1px solid #e9ecef", whiteSpace: "nowrap", background: "#f9fafb",
};
const TD = { padding: "10px 14px", verticalAlign: "middle", borderBottom: "1px solid #f5f5f5" };

/* ─── HELPERS ────────────────────────────────────────────────── */
function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function computeCompletion(p) {
  if (!p) return 0;
  const checks = [
    p.user?.name, p.user?.phone,
    p.dob, p.gender, p.fatherName,
    p.currentAddress, p.city, p.state, p.pincode,
    p.tradition, p.yearsExperience != null,
    p.languages?.length > 0,
    p.pujaTypes?.length > 0,
    p.aadharUrl, p.panUrl,
    p.accountNumber,
  ];
  return Math.round(checks.filter(Boolean).length / checks.length * 100);
}

function computeSLA(createdAt) {
  if (!createdAt) return "—";
  const mins = Math.floor((Date.now() - new Date(createdAt)) / 60000);
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function appId(p) {
  return "APP" + String(p.id || 0).padStart(4, "0");
}

/* ─── STAT CARD ──────────────────────────────────────────────── */
function StatCard({ statusKey, count, active, onClick }) {
  const cfg = STATUS_CONFIG[statusKey] || {};
  return (
    <div onClick={onClick} style={{
      flex: "1 1 140px", background: active ? cfg.bg : "#fff",
      border: `1px solid ${active ? cfg.color + "60" : "#e9ecef"}`,
      borderRadius: 10, padding: "14px 16px", cursor: "pointer",
      transition: "all 0.15s", boxShadow: active ? `0 0 0 2px ${cfg.color}20` : "none",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, background: cfg.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <i className={`fa ${cfg.icon}`} style={{ fontSize: 16, color: cfg.color }} />
        </div>
        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500, lineHeight: 1.2 }}>{cfg.label}</span>
      </div>
      <div style={{ fontSize: "26px", fontWeight: 700, color: "#111827", lineHeight: 1 }}>{count ?? 0}</div>
    </div>
  );
}

/* ─── PIPELINE ───────────────────────────────────────────────── */
function Pipeline({ counts }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #e9ecef", borderRadius: 10,
      padding: "18px 24px", marginBottom: 16,
      display: "flex", alignItems: "center",
    }}>
      {PIPELINE_KEYS.map((key, i) => {
        const cfg = STATUS_CONFIG[key];
        return (
          <React.Fragment key={key}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%", background: cfg.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 8px",
              }}>
                <i className={`fa ${cfg.icon}`} style={{ fontSize: 20, color: cfg.color }} />
              </div>
              <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: 3 }}>{cfg.label}</div>
              <div style={{ fontSize: "22px", fontWeight: 700, color: cfg.color }}>{counts[key] ?? 0}</div>
            </div>
            {i < PIPELINE_KEYS.length - 1 && (
              <div style={{ color: "#d1d5db", fontSize: "14px", padding: "0 4px", paddingBottom: 18, letterSpacing: 1 }}>
                ···→
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─── COMPLETION BAR ─────────────────────────────────────────── */
function CompletionBar({ pct }) {
  const color = pct >= 90 ? "#22c55e" : pct >= 60 ? "#f97316" : "#ef4444";
  return (
    <div>
      <div style={{ fontSize: "11px", color: "#374151", marginBottom: 3, fontWeight: 500 }}>{pct}%</div>
      <div style={{ height: 5, background: "#e9ecef", borderRadius: 3, width: 72 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 3 }} />
      </div>
    </div>
  );
}

/* ─── ONBOARDING CHECKLIST ───────────────────────────────────── */
function OnboardingChecklist({ p }) {
  const items = [
    ["Personal Details",    !!(p.dob && p.gender)],
    ["Service Location",    !!(p.serviceableCities?.length || p.basePincode)],
    ["Address Details",     !!(p.currentAddress && p.city)],
    ["Service Radius",      !!p.serviceRadiusKm],
    ["Professional Details",!!(p.tradition && p.yearsExperience != null)],
    ["Documents",           !!(p.aadharUrl && p.panUrl)],
    ["Puja Expertise",      !!(p.pujaTypes?.length)],
    ["Bank Details",        !!(p.accountNumber && p.ifscCode)],
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 10px", margin: "6px 0 8px" }}>
      {items.map(([label, done]) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "11px", color: done ? "#374151" : "#9ca3af" }}>
          <i className={`fa ${done ? "fa-check-circle" : "fa-circle-o"}`}
            style={{ color: done ? "#22c55e" : "#d1d5db", fontSize: 12, flexShrink: 0 }} />
          {label}
        </div>
      ))}
    </div>
  );
}

/* ─── VERIFICATION STEPS ─────────────────────────────────────── */
function VerificationSteps({ status }) {
  const steps = [
    "Application\nSubmitted",
    "Document\nVerification",
    "Approval\nPending",
    "Activation\nPending",
  ];
  const activeStep =
    status === "ACTIVE"   ? 4 :
    status === "APPROVED" ? 3 :
    (status === "UNDER_REVIEW" || status === "CORRECTION_REQUIRED") ? 2 : 1;

  return (
    <div style={{ display: "flex", alignItems: "flex-start", marginTop: 8 }}>
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < activeStep;
        const current = n === activeStep;
        return (
          <React.Fragment key={i}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{
                width: 26, height: 26, borderRadius: "50%", margin: "0 auto 4px",
                background: done ? "#22c55e" : current ? "#3b82f6" : "#e9ecef",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "10px", fontWeight: 700,
                color: done || current ? "#fff" : "#9ca3af",
              }}>
                {done ? <i className="fa fa-check" style={{ fontSize: 9 }} /> : n}
              </div>
              <div style={{ fontSize: "9px", color: current ? "#3b82f6" : "#9ca3af", lineHeight: 1.3, whiteSpace: "pre-line" }}>
                {label}
              </div>
              {current && <div style={{ fontSize: "8px", color: "#3b82f6", fontWeight: 700 }}>In Progress</div>}
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 0, width: 20, height: 2,
                background: i < activeStep - 1 ? "#22c55e" : "#e9ecef",
                marginTop: 12,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─── RIGHT PANEL ────────────────────────────────────────────── */
function RightPanel({ pandit, onClose, onReviewDone }) {
  const [tab, setTab] = useState("overview");
  const [docStatuses, setDocStatuses] = useState(pandit.documentStatuses || {});
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);


  const user = pandit.user || {};
  const status = pandit.applicationStatus || "DRAFT";
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  const completion = computeCompletion(pandit);

  const handleSubmit = (overrideStatus) => {
    const s = overrideStatus || status;
    setSubmitting(true);
    ApiService.put(`pandit-verification/${pandit.userId}/review`, {
      applicationStatus: s,
      documentStatuses: docStatuses,
      overallRemark: remark,
    }).then(() => {
      setSubmitting(false);
      toast.success("Review submitted");
      onReviewDone();
    }).catch(() => {
      setSubmitting(false);
      toast.error("Failed to submit review");
    });
  };

  const TABS = ["overview", "documents", "details", "timeline"];

  return (
    <div style={{
      background: "#fff", border: "1px solid #e9ecef", borderRadius: 10,
      position: "sticky", top: 16,
      maxHeight: "calc(100vh - 90px)",
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Panel Header */}
      <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          {user.profileImage ? (
            <img src={user.profileImage} alt="profile"
              style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <div style={{
              width: 50, height: 50, borderRadius: "50%", background: "#e9ecef",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, fontWeight: "bold", color: "#6c757d", flexShrink: 0,
            }}>
              {user.name?.charAt(0)?.toUpperCase() || "P"}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
              <span style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{user.name || "—"}</span>
              <span className={`badge light ${cfg.cls}`} style={{ fontSize: "9px" }}>{cfg.short}</span>
            </div>
            <div style={{ fontSize: "11px", color: "#6b7280" }}>
              {appId(pandit)} · {formatDate(pandit.createdAt)}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 16,
            color: "#9ca3af", cursor: "pointer", padding: "0 0 0 4px", flexShrink: 0,
          }}>✕</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: "none", border: "none",
            borderBottom: tab === t ? "2px solid #3b82f6" : "2px solid transparent",
            padding: "7px 10px", fontSize: "11px", fontWeight: tab === t ? 600 : 400,
            color: tab === t ? "#3b82f6" : "#6b7280", cursor: "pointer", textTransform: "capitalize",
          }}>{t}</button>
        ))}
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            <div style={{ fontWeight: 600, fontSize: "13px", color: "#111827", marginBottom: 8 }}>Profile Summary</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", marginBottom: 14 }}>
              {[
                ["fa-phone",    "Phone",      user.phone],
                ["fa-clock-o",  "Experience", pandit.yearsExperience != null ? `${pandit.yearsExperience} Years` : "—"],
                ["fa-map-marker","City",       [pandit.city, pandit.state].filter(Boolean).join(", ") || "—"],
                ["fa-language", "Languages",  (pandit.languages || []).join(", ") || "—"],
              ].map(([icon, label, val]) => (
                <div key={label}>
                  <div style={{ fontSize: "9px", color: "#9ca3af", textTransform: "uppercase", marginBottom: 2 }}>
                    <i className={`fa ${icon}`} style={{ marginRight: 3 }} />{label}
                  </div>
                  <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>{val || "—"}</div>
                </div>
              ))}
            </div>

            <div style={{ fontWeight: 600, fontSize: "13px", color: "#111827", marginBottom: 2 }}>Onboarding Progress</div>
            <OnboardingChecklist p={pandit} />
            <div style={{ height: 6, background: "#e9ecef", borderRadius: 3 }}>
              <div style={{ height: "100%", width: `${completion}%`, background: "#22c55e", borderRadius: 3, transition: "width 0.4s" }} />
            </div>
            <div style={{ fontSize: "10px", color: "#22c55e", textAlign: "right", marginTop: 2, fontWeight: 600 }}>
              {completion}% Completed
            </div>

            <div style={{ fontWeight: 600, fontSize: "13px", color: "#111827", margin: "12px 0 4px" }}>Verification Progress</div>
            <VerificationSteps status={status} />
          </>
        )}

        {/* DOCUMENTS */}
        {tab === "documents" && (
          <>
            <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ fontSize: "9px", color: "#9ca3af", textTransform: "uppercase", marginBottom: 5 }}>Profile Photo</div>
              {user.profileImage ? (
                <a href={user.profileImage} target="_blank" rel="noopener noreferrer">
                  <img src={user.profileImage} alt="profile"
                    style={{ width: "100%", maxHeight: 100, objectFit: "cover", borderRadius: 6, border: "1px solid #e9ecef" }} />
                </a>
              ) : <span style={{ fontSize: "11px", color: "#9ca3af" }}>Not uploaded</span>}
            </div>

            {DOCUMENTS.map(doc => {
              const url = pandit[doc.urlField];
              const docStatus = docStatuses[doc.key] || "PENDING";
              const dsCfg = DOC_STATUS_CONFIG[docStatus] || DOC_STATUS_CONFIG.PENDING;
              const isPdf = url?.toLowerCase().includes(".pdf");

              return (
                <div key={doc.key} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #f0f0f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>{doc.label}</span>
                    <span className={`badge light ${dsCfg.cls}`} style={{ fontSize: "9px" }}>{dsCfg.label}</span>
                  </div>

                  {url ? (isPdf ? (
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="btn btn-outline-secondary btn-xs w-100" style={{ marginBottom: 5 }}>
                      <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />View PDF
                    </a>
                  ) : (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={doc.label}
                        style={{ width: "100%", maxHeight: 85, objectFit: "cover", borderRadius: 5, border: "1px solid #e9ecef", marginBottom: 5 }} />
                    </a>
                  )) : <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: 5 }}>Not uploaded</p>}

                  <div style={{ display: "flex", gap: 5 }}>
                    <button
                      className={`btn btn-xs ${docStatus === "APPROVED" ? "btn-success" : "btn-outline-success"} flex-fill`}
                      onClick={() => setDocStatuses(prev => ({ ...prev, [doc.key]: "APPROVED" }))}>Approve</button>
                    <button
                      className={`btn btn-xs ${docStatus === "REJECTED" ? "btn-danger" : "btn-outline-danger"} flex-fill`}
                      onClick={() => setDocStatuses(prev => ({ ...prev, [doc.key]: "REJECTED" }))}>Reject</button>
                  </div>

                </div>
              );
            })}
          </>
        )}

        {/* DETAILS */}
        {tab === "details" && (
          <div style={{ fontSize: "12px" }}>
            {[
              ["Personal", [
                ["Date of Birth", pandit.dob],
                ["Gender",        pandit.gender],
                ["Father's Name", pandit.fatherName],
              ]],
              ["Address", [
                ["Address",  pandit.currentAddress],
                ["Landmark", pandit.landmark],
                ["City",     pandit.city],
                ["State",    pandit.state],
                ["Pincode",  pandit.pincode],
              ]],
              ["Professional", [
                ["Tradition",    pandit.tradition],
                ["Experience",   pandit.yearsExperience != null ? `${pandit.yearsExperience} yrs` : null],
                ["Approx Pujas", pandit.approxPujasPerformed],
                ["Service Mode", pandit.mode],
                ["Languages",    (pandit.languages || []).join(", ")],
                ["Puja Types",   (pandit.pujaTypes || []).join(", ")],
              ]],
              ["Service Location", [
                ["Base Pincode",   pandit.basePincode],
                ["Service Radius", pandit.serviceRadiusKm ? `${pandit.serviceRadiusKm} km` : null],
                ["Latitude",       pandit.baseLat != null ? String(pandit.baseLat) : null],
                ["Longitude",      pandit.baseLng != null ? String(pandit.baseLng) : null],
              ]],
              ["Bank Details", [
                ["Account Holder", pandit.accountHolderName],
                ["Account No.",    pandit.accountNumber],
                ["IFSC",           pandit.ifscCode],
                ["Bank",           pandit.bankName],
              ]],
            ].map(([section, fields]) => (
              <div key={section} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: "9px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", marginBottom: 5 }}>{section}</div>
                {fields.map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, gap: 8 }}>
                    <span style={{ color: "#9ca3af", flexShrink: 0 }}>{label}</span>
                    <span style={{ color: "#374151", textAlign: "right" }}>{val || "—"}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* TIMELINE */}
        {tab === "timeline" && (
          <div>
            {[
              { icon: "fa-user-plus",   color: "#8b5cf6", label: "Application Submitted", time: formatDate(pandit.createdAt), done: true },
              { icon: "fa-clock-o",     color: "#f97316", label: "Under Review",           time: status !== "DRAFT" ? formatDate(pandit.updatedAt) : "Pending", done: status !== "DRAFT" },
              { icon: "fa-check-circle",color: "#22c55e", label: "Approved",               time: ["APPROVED","ACTIVE"].includes(status) ? formatDate(pandit.updatedAt) : "Pending", done: ["APPROVED","ACTIVE"].includes(status) },
              { icon: "fa-users",       color: "#3b82f6", label: "Activated",              time: status === "ACTIVE" ? formatDate(pandit.updatedAt) : "Pending", done: status === "ACTIVE" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: item.done ? item.color + "20" : "#f3f4f6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className={`fa ${item.icon}`} style={{ fontSize: 13, color: item.done ? item.color : "#d1d5db" }} />
                  </div>
                  {i < 3 && <div style={{ width: 1, height: 24, background: "#e9ecef", margin: "3px 0" }} />}
                </div>
                <div style={{ paddingTop: 5 }}>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: item.done ? "#374151" : "#9ca3af" }}>{item.label}</div>
                  <div style={{ fontSize: "10px", color: "#9ca3af" }}>{item.time}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0", flexShrink: 0, background: "#fafafa" }}>
        <textarea className="form-control form-control-sm" rows={2}
          placeholder="Add overall remark (optional)..."
          value={remark} onChange={e => setRemark(e.target.value)}
          style={{ marginBottom: 8 }} />

        {/* Rejected doc warning */}
        {Object.values(docStatuses).some(s => s === "REJECTED") && (
          <div style={{
            background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 5,
            padding: "6px 10px", marginBottom: 8, fontSize: "11px", color: "#856404",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <i className="fa fa-exclamation-triangle" />
            One or more documents are rejected — approval is blocked.
          </div>
        )}

        <div style={{ display: "flex", gap: 6 }}>
          <button
            className="btn btn-success btn-sm flex-fill"
            disabled={submitting || Object.values(docStatuses).some(s => s === "REJECTED")}
            onClick={() => handleSubmit("ACTIVE")}
            style={{ fontSize: "11px" }}
            title={Object.values(docStatuses).some(s => s === "REJECTED") ? "Resolve rejected documents before approving" : ""}
          >
            <i className="fa fa-check" style={{ marginRight: 4 }} />Approve
          </button>
          <button className="btn btn-warning btn-sm flex-fill" disabled={submitting}
            onClick={() => handleSubmit("CORRECTION_REQUIRED")} style={{ fontSize: "11px" }}>
            <i className="fa fa-pencil" style={{ marginRight: 4 }} />Correction
          </button>
          <button className="btn btn-danger btn-sm flex-fill" disabled={submitting}
            onClick={() => handleSubmit("REJECTED")} style={{ fontSize: "11px" }}>
            <i className="fa fa-times" style={{ marginRight: 4 }} />Reject
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
export default function PanditVerification() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [pandits, setPandits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedPandit, setSelectedPandit] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get("status") || "DRAFT");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fetchTick, setFetchTick] = useState(0);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (p) => {
    const confirmed = window.confirm(
      `⚠️ DEV ONLY — This will permanently delete:\n\n` +
      `Name: ${p.user?.name || "—"}\nPhone: ${p.user?.phone || "—"}\n\n` +
      `All related records (check-ins, OTP sessions, bookings) will also be deleted.\n\n` +
      `Are you sure?`
    );
    if (!confirmed) return;

    setDeletingId(p.userId);
    try {
      await ApiService.delete(`pandit-verification/${p.userId}`);
      toast.success(`Pandit ${p.user?.name} deleted. Phone is free to re-register.`);
      if (selectedPandit?.userId === p.userId) closePanel();
      fetchCounts();
      setFetchTick(t => t + 1);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchCounts = useCallback(() => {
    ApiService.get("pandit-verification/status-counts")
      .then(d => { if (d?.result) setStatusCounts(d.result); })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: currentPage + 1, limit: 20 });
    if (selectedStatus) params.set("status", selectedStatus);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);

    ApiService.get(`pandit-verification?${params}`)
      .then(d => {
        setLoading(false);
        if (d?.result?.data) {
          setPandits(d.result.data);
          setPageCount(d.result.totalPages);
        } else {
          setPandits([]);
          setPageCount(0);
        }
      })
      .catch(() => { setLoading(false); toast.error("Failed to fetch pandits"); });
  }, [currentPage, selectedStatus, debouncedSearch, dateFrom, dateTo, fetchTick]);

  const handleStatClick = (key) => {
    const next = selectedStatus === key ? "" : key;
    setSelectedStatus(next);
    setCurrentPage(0);
    setSelectedPandit(null);
    if (next) setSearchParams({ status: next });
    else setSearchParams({});
  };

  const openPanel = (p) => setSelectedPandit(p);
  const closePanel = () => setSelectedPandit(null);

  const handleReviewDone = () => {
    closePanel();
    fetchCounts();
    setFetchTick(t => t + 1);
  };

  const panelOpen = !!selectedPandit;

  return (
    <div>
      {loading && <Loader />}

      {/* DEV-ONLY Banner */}
      <div style={{
        background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 8,
        padding: "8px 14px", marginBottom: 14,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <i className="fa fa-warning" style={{ color: "#b45309", fontSize: 14 }} />
        <span style={{ fontSize: "12px", color: "#92400e", fontWeight: 600 }}>
          DEV / TESTING ONLY — The delete button permanently removes a pandit and all related data. Do not use in production.
        </span>
      </div>

      {/* Page Title */}
      <div style={{ marginBottom: 14 }}>
        <h4 style={{ fontWeight: 700, color: "#111827", margin: "0 0 2px" }}>Pandit Onboarding</h4>
        <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>
          Manage pandit applications, verification and activation
        </p>
      </div>

      {/* Stat Cards */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        {STAT_ORDER.map(key => (
          <StatCard
            key={key}
            statusKey={key}
            count={statusCounts[key] ?? 0}
            active={selectedStatus === key}
            onClick={() => handleStatClick(key)}
          />
        ))}
      </div>


      {/* Content Row */}
      <div className="row" style={{ margin: "0 -6px" }}>

        {/* Table */}
        <div className={panelOpen ? "col-xl-7 col-lg-7 col-md-12" : "col-xl-12"} style={{ padding: "0 6px" }}>
          <div style={{ background: "#fff", border: "1px solid #e9ecef", borderRadius: 10, overflow: "hidden" }}>

            {/* Table Toolbar */}
            <div style={{
              padding: "12px 16px", borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap",
            }}>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#111827" }}>
                {selectedStatus ? (STATUS_CONFIG[selectedStatus]?.label || "Applications") : "All Applications"}
                {statusCounts[selectedStatus] != null && (
                  <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>
                    ({statusCounts[selectedStatus]})
                  </span>
                )}
              </h5>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div className="input-group input-group-sm" style={{ width: 210 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search name or phone..."
                    value={searchTerm}
                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(0); }}
                  />
                  <span className="input-group-text"><i className="flaticon-381-search-2" /></span>
                </div>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: 138 }}
                  value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setCurrentPage(0); }}
                  title="From date"
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: 138 }}
                  value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setCurrentPage(0); }}
                  title="To date"
                />
                {(dateFrom || dateTo) && (
                  <button
                    className="btn btn-xs btn-outline-secondary"
                    onClick={() => { setDateFrom(""); setDateTo(""); setCurrentPage(0); }}
                    title="Clear date filter"
                  >✕</button>
                )}
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table className="table mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th style={TH}>Pandit Name</th>
                    {!panelOpen && <><th style={TH}>City</th><th style={TH}>App ID</th></>}
                    <th style={TH}>Submitted On</th>
                    <th style={TH}>Completion</th>
                    <th style={TH}>Stage</th>
                    <th style={TH}>SLA</th>
                    <th style={TH}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pandits.length > 0 ? pandits.map(p => {
                    const st = p.applicationStatus || "DRAFT";
                    const c = STATUS_CONFIG[st] || STATUS_CONFIG.DRAFT;
                    const pct = computeCompletion(p);
                    const isSelected = selectedPandit?.userId === p.userId;

                    return (
                      <tr key={p.id} style={{ background: isSelected ? "#eff6ff" : undefined, transition: "background 0.1s" }}>
                        <td style={TD}>
                          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                            {p.user?.profileImage ? (
                              <img src={p.user.profileImage} alt="p"
                                style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                            ) : (
                              <div style={{
                                width: 32, height: 32, borderRadius: "50%", background: "#e9ecef",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 13, fontWeight: "bold", color: "#6c757d", flexShrink: 0,
                              }}>
                                {p.user?.name?.charAt(0)?.toUpperCase() || "P"}
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 500, color: "#111827" }}>{p.user?.name || "—"}</div>
                              {panelOpen && <div style={{ fontSize: "10px", color: "#9ca3af" }}>{p.user?.phone}</div>}
                            </div>
                          </div>
                        </td>
                        {!panelOpen && (
                          <>
                            <td style={TD}>{p.city || "—"}</td>
                            <td style={TD}>
                              <span style={{ fontFamily: "monospace", fontSize: "11px", color: "#6b7280" }}>{appId(p)}</span>
                            </td>
                          </>
                        )}
                        <td style={TD}>
                          <span style={{ fontSize: "11px", color: "#6b7280" }}>{formatDate(p.createdAt)}</span>
                        </td>
                        <td style={TD}><CompletionBar pct={pct} /></td>
                        <td style={TD}>
                          <span className={`badge light ${c.cls}`} style={{ fontSize: "10px", whiteSpace: "nowrap" }}>{c.short}</span>
                        </td>
                        <td style={TD}>
                          <span style={{ fontSize: "11px", fontWeight: 600, color: "#ef4444" }}>{computeSLA(p.createdAt)}</span>
                        </td>
                        <td style={TD}>
                          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                            <button
                              className={`btn btn-xs ${isSelected ? "btn-secondary" : "btn-outline-primary"}`}
                              onClick={() => isSelected ? closePanel() : openPanel(p)}
                            >
                              {isSelected ? "Close" : "Review"}
                            </button>
                            <button
                              className="btn btn-xs btn-danger"
                              title="DEV ONLY — Permanently delete this pandit"
                              disabled={deletingId === p.userId}
                              onClick={() => handleDelete(p)}
                              style={{ opacity: 0.85 }}
                            >
                              {deletingId === p.userId
                                ? <i className="fa fa-spinner fa-spin" />
                                : <i className="fa fa-trash" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={panelOpen ? 6 : 8} className="text-center py-4" style={{ color: "#9ca3af" }}>
                        No applications found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div style={{
              padding: "10px 16px", borderTop: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Showing {pandits.length} of {statusCounts[selectedStatus] ?? pandits.length} results
              </span>
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  handlePageClick={({ selected }) => { setCurrentPage(selected); setSelectedPandit(null); }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right Panel */}
        {panelOpen && (
          <div className="col-xl-5 col-lg-5 col-md-12" style={{ padding: "0 6px" }}>
            <RightPanel
              key={selectedPandit.userId}
              pandit={selectedPandit}
              onClose={closePanel}
              onReviewDone={handleReviewDone}
            />
          </div>
        )}

      </div>
    </div>
  );
}
